import { PlayerData } from './kyp-search';

// Regular expression to find article references in the format "Title of Article" or similar patterns
const ARTICLE_REFERENCE_REGEX = /"([^"]+)"|'([^']+)'|"([^"]+)"|"([^"]+)"|'([^']+)'|«([^»]+)»/g;

// Flag to identify if results are from the stacked KYP pipeline
function isStackedKYPResult(results: any): boolean {
  return (
    results && 
    typeof results === 'object' && 
    (results.individual_summaries !== undefined || 
     (results.metadata && 
      (results.metadata.pipeline_type === 'stacked_kyp_json' || 
       results.metadata.pipeline_type === 'stacked' || 
       results.metadata.pipeline_type === 'summaries_only' ||
       results.metadata.processing_type === 'enhanced_implementation_with_json_relevance_filtering_and_player_facts')))
  );
}

// Check if the response is already in the legacy format
function isLegacyFormatResponse(results: any): boolean {
  // Import PLAYER_SECTIONS from player.ts
  const PLAYER_SECTIONS = [
    'family', 'financial', 'career', 'personal_life', 'agent', 
    'personality', 'early_career_and_education', 'adverse_media', 
    'legal_and_criminal_history', 'sponsorships'
  ];
  
  // Check if the results have at least some of the expected category structure
  const hasExpectedStructure = results && 
         typeof results === 'object' &&
         PLAYER_SECTIONS.some(section => 
           results[section] && 
           typeof results[section] === 'object' &&
           (
             (results[section].summary !== undefined) ||
             (results[section].rating !== undefined)
           )
         );
  
  if (hasExpectedStructure) {
    // Log which categories are present for debugging
    const presentCategories = PLAYER_SECTIONS.filter(section => 
      results[section] && 
      typeof results[section] === 'object' &&
      (
        (results[section].summary !== undefined) ||
        (results[section].rating !== undefined)
      )
    );
    console.log(`Response is in legacy format. Present categories: ${presentCategories.join(', ')}`);
    
    // Log which categories are missing
    const missingCategories = PLAYER_SECTIONS.filter(section => 
      !results[section] || 
      typeof results[section] !== 'object' ||
      (
        (results[section].summary === undefined) &&
        (results[section].rating === undefined)
      )
    );
    if (missingCategories.length > 0) {
      console.log(`Missing categories in legacy format: ${missingCategories.join(', ')}`);
    }
  }
  
  return hasExpectedStructure;
}

// Process search results to convert string JSON to proper objects
export function processSearchResults(results: any): PlayerData {
  // Check if this is a stacked KYP result
  if (isStackedKYPResult(results)) {
    console.log('Processing stacked KYP result');
    return processStackedKYPResults(results);
  }
  
  // Otherwise, process as legacy KYP result
  console.log('Processing legacy KYP result');
  const processedData: PlayerData = {};
  
  // First, check for overall_player_score at the top level
  if (results.overall_player_score !== undefined) {
    processedData.overall_player_score = results.overall_player_score;
  }
  
  // Extract all article references from the original data
  const articleReferences: Record<string, string> = {};
  
  // Look for article URLs in the original data
  if (results.articles && Array.isArray(results.articles)) {
    console.log('Found articles array with', results.articles.length, 'items');
    results.articles.forEach((article: any) => {
      if (article.title && article.url) {
        articleReferences[article.title.trim()] = article.url;
      }
    });
  }
  
  // Also check for article_references directly in the results
  if (results.article_references && typeof results.article_references === 'object') {
    console.log('Found article_references object with', Object.keys(results.article_references).length, 'items');
    Object.entries(results.article_references).forEach(([title, url]) => {
      if (title && url) {
        articleReferences[title.trim()] = url as string;
      }
    });
  }
  
  // Check for articles in the debug section
  if (results.debug && results.debug.all_articles && Array.isArray(results.debug.all_articles)) {
    console.log('Found all_articles in debug section with', results.debug.all_articles.length, 'items');
    
    // Log detailed information about the articles for debugging
    console.log('Article source information:');
    results.debug.all_articles.forEach((article: any, index: number) => {
      if (article.title && article.url) {
        articleReferences[article.title.trim()] = article.url;
        
        // Log detailed information about each article
        console.log(`Article ${index + 1}: ${article.title}`);
        console.log(`  - from_database: ${article.from_database}`);
        console.log(`  - historical: ${article.historical}`);
        console.log(`  - scrape_date: ${article.scrape_date}`);
      }
    });
    
    // Preserve the debug section with all_articles
    processedData.debug = {
      ...results.debug,
      all_articles: results.debug.all_articles
    };
  }
  
  console.log('Extracted article references:', articleReferences);
  
  // Process each category in the results
  Object.entries(results).forEach(([key, value]) => {
    // Skip the overall_player_score, articles, article_references, and debug as we've already processed them
    if (key === 'overall_player_score' || key === 'status' || key === 'articles' || key === 'article_references' || key === 'debug') {
      return;
    }
    
    // Special handling for all_referenced_articles
    if (key === 'all_referenced_articles' && typeof value === 'object' && value !== null) {
      processedData.all_referenced_articles = value;
      console.log('Found all_referenced_articles with', Object.keys(value).length, 'items');
      return;
    }
    
    if (typeof value === 'string') {
      try {
        // Try to parse the string as JSON
        const parsedValue = JSON.parse(value);
        
        // Special handling for rating data
        if (parsedValue && typeof parsedValue === 'object' && parsedValue.rating) {
          // If rating is a string, try to parse it
          if (typeof parsedValue.rating === 'string') {
            try {
              parsedValue.rating = JSON.parse(parsedValue.rating);
            } catch (e) {
              // If parsing fails, leave it as a string
              console.log(`Failed to parse rating string for ${key}:`, e);
            }
          }
        }
        
        // Check if this section has its own article_references
        if (parsedValue && typeof parsedValue === 'object' && parsedValue.article_references) {
          console.log(`Found article_references in ${key} section`);
          // Keep the section's own article references
        } else {
          // Add article references to the summary if they exist
          if (parsedValue && typeof parsedValue === 'object' && parsedValue.summary) {
            // Store the original summary
            const originalSummary = typeof parsedValue.summary === 'string' 
              ? parsedValue.summary 
              : parsedValue.summary.summary || '';
            
            // Add article_references if we have any matches
            if (Object.keys(articleReferences).length > 0) {
              parsedValue.article_references = extractArticleReferences(originalSummary, articleReferences);
            }
          }
        }
        
        processedData[key as keyof PlayerData] = parsedValue;
      } catch (e) {
        // If parsing fails, use the string value directly
        console.log(`Failed to parse JSON for ${key}:`, e);
        const summaryObj = { summary: value };
        
        // Add article references if we have any matches
        if (Object.keys(articleReferences).length > 0) {
          summaryObj.article_references = extractArticleReferences(value, articleReferences);
        }
        
        processedData[key as keyof PlayerData] = summaryObj;
      }
    } else if (typeof value === 'object' && value !== null) {
      // If it's already an object, use it directly
      const processedValue = { ...value };
      
      // Check if this section has its own article_references
      if (!processedValue.article_references) {
        // Add article references if we have any matches and there's a summary
        if (Object.keys(articleReferences).length > 0 && processedValue.summary) {
          const summaryText = typeof processedValue.summary === 'string' 
            ? processedValue.summary 
            : processedValue.summary?.summary || '';
            
          if (summaryText) {
            processedValue.article_references = extractArticleReferences(summaryText, articleReferences);
          }
        }
      }
      
      processedData[key as keyof PlayerData] = processedValue;
    } else {
      // For other types (like null, undefined, etc.), just pass through
      processedData[key as keyof PlayerData] = value as any;
    }
  });
  
  // Store the article references at the top level as well
  if (Object.keys(articleReferences).length > 0) {
    processedData.article_references = articleReferences;
    console.log('Added article_references to top level with', Object.keys(articleReferences).length, 'items');
  }
  
  // Log the processed data for debugging
  console.log('Processed search results with article references:', processedData);
  
  return processedData;
}

/**
 * Process results from the stacked KYP pipeline
 */
function processStackedKYPResults(results: any): PlayerData {
  // First check if the results are already in the legacy format
  if (isLegacyFormatResponse(results)) {
    console.log('Stacked KYP results are already in legacy format, using directly');
    
    // Create a PlayerData object from the results
    const playerData: PlayerData = {
      ...results,
      overall_player_score: results.overall_player_score || 0
    };
    
    // Store individual summaries in the debug section for reference
    if (results.individual_summaries && Array.isArray(results.individual_summaries)) {
      if (!playerData.debug) {
        playerData.debug = {};
      }
      playerData.debug.individual_summaries = results.individual_summaries;
    }
    
    return playerData;
  }
  
  // If not in legacy format, use the adapter function
  try {
    // Use dynamic import to avoid circular dependencies
    const { adaptStackedKYPResults } = require('./kyp-search/utils/result-adapters');
    
    // Use the adapter to convert stacked KYP results to PlayerData format
    const adaptedResults = adaptStackedKYPResults(results);
    
    // Store individual summaries in the debug section for reference
    if (results.individual_summaries && Array.isArray(results.individual_summaries)) {
      if (!adaptedResults.debug) {
        adaptedResults.debug = {};
      }
      adaptedResults.debug.individual_summaries = results.individual_summaries;
    }
    
    return adaptedResults;
  } catch (error) {
    console.error('Error processing stacked KYP results:', error);
    
    // Return a minimal PlayerData object with an error message
    return {
      overall_player_score: 0,
      career: {
        summary: 'Error processing stacked KYP results. Please try again.',
        rating: {
          rating: 0,
          explanation: 'Error processing results'
        }
      },
      debug: {
        error: error instanceof Error ? error.message : 'Unknown error',
        raw_results: results
      }
    };
  }
}

/**
 * Extracts article references from a text by looking for quoted titles
 * and matching them against known article titles
 */
function extractArticleReferences(text: string, articleReferences: Record<string, string>): Record<string, string> {
  const foundReferences: Record<string, string> = {};
  const articleTitles = Object.keys(articleReferences);
  
  // First try to find exact matches of article titles in the text
  articleTitles.forEach(title => {
    if (text.includes(title)) {
      foundReferences[title] = articleReferences[title];
    }
  });
  
  // Then look for quoted text that might be article references
  let match;
  const regex = new RegExp(ARTICLE_REFERENCE_REGEX); // Create a new regex instance to reset lastIndex
  while ((match = regex.exec(text)) !== null) {
    // The regex has multiple capture groups for different quote styles
    // Find the one that matched
    const quotedText = match[1] || match[2] || match[3] || match[4] || match[5] || match[6];
    
    if (quotedText) {
      // Look for the closest matching article title
      const bestMatch = findBestMatch(quotedText, articleTitles);
      if (bestMatch) {
        foundReferences[quotedText] = articleReferences[bestMatch];
      }
    }
  }
  
  return foundReferences;
}

/**
 * Finds the best matching article title for a quoted reference
 */
function findBestMatch(quotedText: string, articleTitles: string[]): string | null {
  // First try exact match
  if (articleTitles.includes(quotedText)) {
    return quotedText;
  }
  
  // Then try case-insensitive match
  const lowerQuoted = quotedText.toLowerCase();
  for (const title of articleTitles) {
    if (title.toLowerCase() === lowerQuoted) {
      return title;
    }
  }
  
  // Then try partial match (quoted text is part of a title)
  for (const title of articleTitles) {
    if (title.toLowerCase().includes(lowerQuoted)) {
      return title;
    }
  }
  
  // Then try if title is part of quoted text
  for (const title of articleTitles) {
    if (lowerQuoted.includes(title.toLowerCase())) {
      return title;
    }
  }
  
  // No good match found
  return null;
}